import React, { useCallback, useEffect, useState } from "react";
import { Loader2, KeyRound, Pencil, Trash2, Save, X, Bot, Building2, Activity, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  companyLlmGet, companyLlmSave, companyLlmUsage, companyAgentLlmSave, companyAgentLlmDelete,
  companyLlmBackupSave, companyLlmBackupDelete, companyAgentLlmBackupSave, companyAgentLlmBackupDelete,
  companyLlmCheck, companyAgentLlmCheck,
} from "@/lib/api";
import { ChainExplainer, Field, UsageBar, errDetail, fmtDate, fmtInt } from "@/components/company/CompanyUi";

const STATUS_LABEL = {
  ok: "Disponible",
  limite_alcanzado: "Límite alcanzado",
  desactivada: "Desactivada",
  sin_limite: "Sin límite válido",
  invalida: "Clave ilegible o proveedor desconocido",
  ausente: "Sin clave",
  historico: "Histórico",
};
const LOW_LIMIT = 20000;

/**
 * Generic company key + one key per agent, with the month's consumption.
 * Used by the admin client record (tab "Claves LLM") and by the master (/mi-empresa/claves).
 * API keys are only sent to the backend; the panel only ever shows the masked value / last 4.
 */
export default function CompanyLlmKeysPanel({ slug, agents = [], forClient = false, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [generic, setGeneric] = useState(null);
  const [byAgent, setByAgent] = useState({});
  const [month, setMonth] = useState("");

  const load = useCallback(async () => {
    try {
      const [g, u] = await Promise.all([companyLlmGet(slug), companyLlmUsage(slug)]);
      setProviders(g.providers || []);
      setGeneric(g.credential || null);
      setMonth(u.month || "");
      const m = {};
      (u.credentials || []).forEach((c) => {
        if (c.scope === "agent" && c.agent_slug) m[c.agent_slug] = c;
      });
      setByAgent(m);
    } catch (e) {
      toast.error(errDetail(e, "No se pudieron cargar las claves de IA."));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = async () => {
    await load();
    if (onChanged) onChanged();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando claves…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-llm-panel">
      <ChainExplainer forClient={forClient} />
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-500" data-testid="llm-month">
        Consumo del mes {month || "—"} (UTC)
      </p>

      <KeyCard
        title="Clave genérica de la empresa"
        subtitle="Se usa para los agentes sin clave propia o cuando la del agente se agota."
        icon={Building2}
        cred={generic}
        providers={providers}
        testid="generic"
        onSave={async (body) => {
          await companyLlmSave(slug, body);
          await afterChange();
        }}
        onSaveBackup={async (body) => { await companyLlmBackupSave(slug, body); await afterChange(); }}
        onDeleteBackup={async () => { await companyLlmBackupDelete(slug); await afterChange(); }}
        onCheck={(body) => companyLlmCheck(slug, body)}
      />

      <div>
        <p className="eyebrow">Claves por agente</p>
        {agents.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500" data-testid="llm-no-agents">
            Esta empresa todavía no tiene agentes. Las claves por agente se configuran cuando existan.
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {agents.map((a) => (
              <KeyCard
                key={a.slug}
                title={a.name || a.slug}
                subtitle={a.department ? `Departamento: ${a.department}` : a.slug}
                icon={Bot}
                cred={byAgent[a.slug] || null}
                providers={providers}
                testid={`agent-${a.slug}`}
                onSave={async (body) => {
                  await companyAgentLlmSave(slug, a.slug, body);
                  await afterChange();
                }}
                onDelete={async () => {
                  await companyAgentLlmDelete(slug, a.slug);
                  await afterChange();
                }}
                onSaveBackup={async (body) => { await companyAgentLlmBackupSave(slug, a.slug, body); await afterChange(); }}
                onDeleteBackup={async () => { await companyAgentLlmBackupDelete(slug, a.slug); await afterChange(); }}
                onCheck={(body) => companyAgentLlmCheck(slug, a.slug, body)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KeyCard({ title, subtitle, icon: Icon, cred, providers, testid, onSave, onDelete, onSaveBackup, onDeleteBackup, onCheck }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [form, setForm] = useState(null);

  const open = () => {
    const p = cred?.provider || providers[0]?.id || "openai";
    setForm({
      provider: p,
      model: cred?.model || providers.find((x) => x.id === p)?.default_model || "",
      api_key: "",
      limit: cred?.monthly_token_limit ? String(cred.monthly_token_limit) : "",
      active: cred ? !!cred.active : true,
      base_url: cred?.base_url || "",
    });
    setEditing(true);
  };
  const close = () => {
    setForm(null); // the typed API key is dropped from memory
    setEditing(false);
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prov = providers.find((x) => x.id === form?.provider);

  const submit = async () => {
    const limit = Number(form.limit);
    if (!form.limit || !Number.isInteger(limit) || limit < 1) {
      toast.error("Indique un límite mensual de tokens (entero mayor que 0). Es obligatorio.");
      return;
    }
    const key = form.api_key.trim();
    if (!cred && !key) {
      toast.error("Indique la API key.");
      return;
    }
    if (cred && !key && form.provider !== cred.provider) {
      toast.error("Al cambiar de proveedor indique también la API key.");
      return;
    }
    const body = { provider: form.provider, model: form.model.trim(), monthly_token_limit: limit, active: form.active,
                   base_url: form.base_url || null };
    if (key) body.api_key = key;
    setSaving(true);
    try {
      await onSave(body);
      toast.success("Clave guardada.");
      close();
    } catch (e) {
      toast.error(errDetail(e, "No se pudo guardar la clave."));
    } finally {
      setSaving(false);
    }
  };

  const u = cred?.usage;
  return (
    <article className="border border-slate-200 bg-white p-5" data-testid={`llm-card-${testid}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center bg-slate-900 text-accent-blue-light">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h4 className="font-display text-lg text-slate-900">{title}</h4>
            <p className="text-[12px] text-slate-500">{subtitle}</p>
          </div>
        </div>
        {!editing && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={open}
                    className="h-8 rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.14em]"
                    data-testid={`llm-edit-${testid}`}>
              {cred ? <><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</> : <><KeyRound className="mr-1 h-3.5 w-3.5" /> Configurar</>}
            </Button>
            {cred && onDelete && (
              <Button size="sm" variant="outline" onClick={() => setConfirmDel(true)}
                      className="h-8 rounded-sm border-red-300 text-[11px] uppercase tracking-[0.14em] text-red-700 hover:bg-red-50"
                      data-testid={`llm-delete-${testid}`}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Quitar
              </Button>
            )}
          </div>
        )}
      </div>

      {!editing && !cred && (
        <p className="mt-4 text-sm text-slate-500" data-testid={`llm-empty-${testid}`}>
          Sin clave configurada.
        </p>
      )}

      {!editing && cred && (
        <div className="mt-4 space-y-3">
          <dl className="grid grid-cols-2 gap-3 text-[12px] md:grid-cols-5">
            <Info k="Proveedor" v={providers.find((x) => x.id === cred.provider)?.label || cred.provider} />
            <Info k="Modelo" v={cred.model || "—"} mono />
            <Info k="API key" v={cred.api_key_last4 ? `•••• ${cred.api_key_last4}` : cred.api_key_masked || "••••"} mono testid={`llm-masked-${testid}`} />
            <Info k="Límite mensual" v={`${fmtInt(cred.monthly_token_limit)} tokens`} mono />
            <Info k="Estado" v={`${cred.active ? "Activa" : "Inactiva"} · ${STATUS_LABEL[cred.status] || cred.status}`} testid={`llm-status-${testid}`} />
          </dl>
          <InUseLine cred={cred} testid={testid} />
          <CheckButton label="Probar clave principal" onRun={() => onCheck?.({ role: "primary" })} testid={`llm-check-${testid}`} />
          {u && (
            <UsageBar used={u.total} limit={cred.monthly_token_limit} pct={u.pct} status={cred.status}
                      testid={`llm-usage-${testid}`} />
          )}
          {u && (
            <p className="font-mono text-[11px] text-slate-500">
              {fmtInt(u.calls)} llamadas · entrada {fmtInt(u.tokens_in)} · salida {fmtInt(u.tokens_out)} ·
              restante {u.remaining == null ? "—" : fmtInt(u.remaining)}
              {u.truncated_replies ? ` · ${fmtInt(u.truncated_replies)} respuesta(s) acortada(s) por el límite` : ""}
              {u.estimated_calls ? ` · ${fmtInt(u.estimated_calls)} estimada(s)` : ""}
            </p>
          )}
          {cred.last_error && (
            <p className="border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
              Último error del proveedor ({fmtDate(cred.last_error.at)}): {cred.last_error.message || cred.last_error.kind}
            </p>
          )}
          <BackupSection cred={cred} providers={providers} testid={testid} onSave={onSaveBackup}
                         onDelete={onDeleteBackup} onCheck={onCheck} />
        </div>
      )}

      {editing && form && (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid={`llm-form-${testid}`}>
          <Field label="Proveedor" required>
            <Select value={form.provider} onValueChange={(v) => {
              upd("provider", v);
              upd("model", providers.find((x) => x.id === v)?.default_model || "");
            }}>
              <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm" data-testid={`llm-provider-${testid}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Modelo" hint="Elija de la lista o escriba otro modelo del proveedor.">
            <Input value={form.model} onChange={(e) => upd("model", e.target.value)} list={`models-${testid}`}
                   className="h-9 rounded-sm border-slate-300 font-mono text-sm" data-testid={`llm-model-${testid}`} />
            <datalist id={`models-${testid}`}>
              {(prov?.models || []).map((m) => <option key={m} value={m} />)}
            </datalist>
          </Field>
          <RegionField prov={prov} value={form.base_url} onChange={(v) => upd("base_url", v)} testid={testid} />
          <Field label="API key" required={!cred}
                 hint={cred ? `Deje vacío para conservar la clave actual (termina en ${cred.api_key_last4 || "…"}).` : `Se guarda cifrada y nunca se vuelve a mostrar.${prov?.key_prefix ? ` Suele empezar por ${prov.key_prefix}` : ""}`}>
            <Input type="password" autoComplete="new-password" value={form.api_key}
                   onChange={(e) => upd("api_key", e.target.value)} placeholder={cred ? "••••••••" : ""}
                   className="h-9 rounded-sm border-slate-300 font-mono text-sm" data-testid={`llm-apikey-${testid}`} />
          </Field>
          <Field label="Límite mensual de tokens" required
                 hint={Number(form.limit) > 0 && Number(form.limit) < LOW_LIMIT
                   ? "Límite muy bajo: cada consulta usa cientos de tokens de entrada; el agente puede quedar bloqueado tras pocas consultas."
                   : "Entrada + salida del mes (UTC). Obligatorio: para clientes no existe 'sin límite'."}>
            <Input type="number" min={1} step={1000} value={form.limit} onChange={(e) => upd("limit", e.target.value)}
                   className="h-9 rounded-sm border-slate-300 font-mono text-sm" data-testid={`llm-limit-${testid}`} />
          </Field>
          <div className="flex items-center gap-3 md:col-span-2">
            <Switch checked={form.active} onCheckedChange={(v) => upd("active", v)} data-testid={`llm-active-${testid}`} />
            <span className="text-sm text-slate-700">{form.active ? "Clave activa" : "Clave inactiva (no se usa)"}</span>
          </div>
          <div className="flex gap-2 md:col-span-2">
            <Button onClick={submit} disabled={saving}
                    className="h-9 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-slate-800"
                    data-testid={`llm-save-${testid}`}>
              {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Guardar
            </Button>
            <Button variant="outline" onClick={close} disabled={saving}
                    className="h-9 rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.16em]"
                    data-testid={`llm-cancel-${testid}`}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar la clave de este agente</AlertDialogTitle>
            <AlertDialogDescription>
              El agente pasará a usar la clave genérica de la empresa (si existe y tiene saldo); si no, quedará bloqueado.
              El consumo del mes se conserva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`llm-delete-cancel-${testid}`}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid={`llm-delete-confirm-${testid}`}
              onClick={async () => {
                try {
                  await onDelete();
                  toast.success("Clave quitada.");
                } catch (e) {
                  toast.error(errDetail(e, "No se pudo quitar la clave."));
                }
              }}
            >
              Quitar clave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}

// ---- fix 12: in-use indicator, «Probar clave», regional endpoint and the slot's backup key ----
function InUseLine({ cred, testid }) {
  const role = cred?.in_use?.role;
  return (
    <p className="text-[12px] text-slate-600" data-testid={`llm-inuse-${testid}`}>
      {role ? <>En uso: <strong>{role === "backup" ? "clave de backup" : "clave principal"}</strong> (desde {fmtDate(cred.in_use.at)})</>
            : "Todavía no se ha usado este mes."}
      {cred?.primary_failing_since && (
        <span className="ml-2 text-red-700">· Principal fallando desde {fmtDate(cred.primary_failing_since)}</span>
      )}
    </p>
  );
}

function CheckButton({ label, onRun, testid }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  if (!onRun) return null;
  const run = async () => {
    setBusy(true);
    try {
      setRes(await onRun());
    } catch (e) {
      setRes({ ok: false, message: errDetail(e, "No se pudo probar la clave.") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button size="sm" variant="outline" onClick={run} disabled={busy}
              className="h-8 rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.14em]" data-testid={testid}>
        {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Activity className="mr-1 h-3.5 w-3.5" />} {label}
      </Button>
      {res && (
        <span className={`text-[12px] ${res.ok ? "text-emerald-700" : "text-red-700"}`} data-testid={`${testid}-result`}>
          {res.ok ? `OK · ${res.model || ""}${res.latency_ms ? ` · ${res.latency_ms} ms` : ""}` : `Falla: ${res.message || res.kind || "error"}`}
        </span>
      )}
    </div>
  );
}

function RegionField({ prov, value, onChange, testid }) {
  const opts = prov?.api_base_options || [];
  if (!opts.length) return null;
  return (
    <Field label="Región del endpoint" hint="Internacional por defecto. Solo se aceptan los endpoints oficiales.">
      <Select value={value || opts[0].url} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm" data-testid={`llm-region-${testid}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opts.map((o) => <SelectItem key={o.url} value={o.url}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
}

function BackupSection({ cred, providers, testid, onSave, onDelete, onCheck }) {
  const b = cred?.backup;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!onSave) return null;
  const tid = `${testid}-backup`;
  const prov = providers.find((x) => x.id === form?.provider);
  const open = () => {
    const p = b?.provider || providers.find((x) => x.id !== cred?.provider)?.id || providers[0]?.id;
    setForm({ provider: p, model: b?.model || providers.find((x) => x.id === p)?.default_model || "", api_key: "",
              base_url: b?.base_url || "" });
  };
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    const key = form.api_key.trim();
    if (!b && !key) { toast.error("Indique la API key de backup."); return; }
    if (b && !key && form.provider !== b.provider) { toast.error("Al cambiar de proveedor indique también la API key."); return; }
    const body = { provider: form.provider, model: form.model.trim(), base_url: form.base_url || null };
    if (key) body.api_key = key;
    setSaving(true);
    try {
      await onSave(body);
      toast.success("Clave de backup guardada.");
      setForm(null);
    } catch (e) {
      toast.error(errDetail(e, "No se pudo guardar la clave de backup."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border-t border-dashed border-slate-200 pt-3" data-testid={`llm-backup-${testid}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[12px] font-medium text-slate-700">
          <LifeBuoy className="h-3.5 w-3.5" /> Clave de backup (opcional)
        </p>
        {!form && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={open}
                    className="h-7 rounded-sm border-slate-300 text-[10px] uppercase tracking-[0.14em]" data-testid={`llm-edit-${tid}`}>
              {b ? "Editar backup" : "Configurar backup"}
            </Button>
            {b && (
              <Button size="sm" variant="outline" onClick={async () => {
                try { await onDelete(); toast.success("Backup quitado."); } catch (e) { toast.error(errDetail(e, "No se pudo quitar.")); }
              }} className="h-7 rounded-sm border-red-300 text-[10px] uppercase tracking-[0.14em] text-red-700" data-testid={`llm-delete-${tid}`}>
                Quitar backup
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Se usa si la clave principal falla (autenticación, saldo, cuota, caída del proveedor). Consume del MISMO límite mensual.
      </p>
      {!form && b && (
        <div className="mt-2 space-y-2">
          <dl className="grid grid-cols-2 gap-3 text-[12px] md:grid-cols-4">
            <Info k="Proveedor" v={providers.find((x) => x.id === b.provider)?.label || b.provider} />
            <Info k="Modelo" v={b.model || "—"} mono />
            <Info k="API key" v={b.api_key_last4 ? `•••• ${b.api_key_last4}` : b.api_key_masked || "••••"} mono testid={`llm-masked-${tid}`} />
            <Info k="Estado" v={b.usable ? "Disponible" : "Clave ilegible"} />
          </dl>
          {cred?.usage?.backup_calls ? (
            <p className="font-mono text-[11px] text-slate-500">
              {fmtInt(cred.usage.backup_calls)} llamada(s) por el backup este mes · {fmtInt(cred.usage.backup_tokens)} tokens
            </p>
          ) : null}
          {b.last_error && (
            <p className="border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800">
              Último error del backup ({fmtDate(b.last_error.at)}): {b.last_error.message || b.last_error.kind}
            </p>
          )}
          <CheckButton label="Probar backup" onRun={() => onCheck?.({ role: "backup" })} testid={`llm-check-${tid}`} />
        </div>
      )}
      {form && (
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid={`llm-form-${tid}`}>
          <Field label="Proveedor" required>
            <Select value={form.provider} onValueChange={(v) => {
              upd("provider", v);
              upd("model", providers.find((x) => x.id === v)?.default_model || "");
              upd("base_url", "");
            }}>
              <SelectTrigger className="h-9 rounded-sm border-slate-300 text-sm" data-testid={`llm-provider-${tid}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Modelo" hint="Elija de la lista o escriba otro modelo del proveedor.">
            <Input value={form.model} onChange={(e) => upd("model", e.target.value)} list={`models-${tid}`}
                   className="h-9 rounded-sm border-slate-300 font-mono text-sm" data-testid={`llm-model-${tid}`} />
            <datalist id={`models-${tid}`}>
              {(prov?.models || []).map((m) => <option key={m} value={m} />)}
            </datalist>
          </Field>
          <RegionField prov={prov} value={form.base_url} onChange={(v) => upd("base_url", v)} testid={tid} />
          <Field label="API key" required={!b} hint={b ? `Deje vacío para conservar la clave actual (termina en ${b.api_key_last4 || "…"}).` : "Se guarda cifrada y nunca se vuelve a mostrar."}>
            <Input type="password" autoComplete="new-password" value={form.api_key}
                   onChange={(e) => upd("api_key", e.target.value)} placeholder={b ? "••••••••" : ""}
                   className="h-9 rounded-sm border-slate-300 font-mono text-sm" data-testid={`llm-apikey-${tid}`} />
          </Field>
          <div className="flex gap-2 md:col-span-2">
            <Button onClick={submit} disabled={saving}
                    className="h-9 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-slate-800"
                    data-testid={`llm-save-${tid}`}>
              {saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />} Guardar backup
            </Button>
            <Button variant="outline" onClick={() => setForm(null)} disabled={saving}
                    className="h-9 rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.16em]" data-testid={`llm-cancel-${tid}`}>
              <X className="mr-1 h-3.5 w-3.5" /> Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ k, v, mono, testid }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{k}</dt>
      <dd className={`mt-0.5 break-words text-slate-900 ${mono ? "font-mono text-[12px]" : ""}`} data-testid={testid}>{v}</dd>
    </div>
  );
}
